import { PRIVILEGES } from 'constants/privileges';
import { useEffect, useMemo, useState } from 'react';
import { Box, Divider, MenuItem, Paper, TextField } from '@mui/material';
import { useGetExternalServiceCodesGrouped } from 'api/externalServiceCode';
import {
  ExternalServiceCodeGrouped,
  MappingParams,
} from 'api/externalServiceCode/externalServiceCode.types';
import { StyledButton } from 'components/Buttons';
import { LoadingScreen } from 'components/Loaders/LoadingScreen';
import { useAuth } from 'contexts/authContext';
import { useNavigate } from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import { ShippingService } from '../mapping.types';
import {
  allCounterparties,
  buttons,
  mainColumnHeaders,
  searchPlaceholder,
} from './MappingModule.consts';
import { MappingTable } from './MappingTable';

const ALL_COUNTERPARTIES = 'all';

function mapToShippingServices(
  grouped: ExternalServiceCodeGrouped[] | undefined,
): ShippingService[] {
  if (!grouped) return [];
  return grouped.map((item) => ({
    id: String(item.activityId),
    serviceName: item.activityCode,
    counterparties: item.counterparties.map((counterparty) => ({
      id: String(counterparty.counterpartyId),
      name: counterparty.counterpartyCode,
      codes: counterparty.externalNames.map((name) => ({
        id: name,
        code: name,
      })),
    })),
  }));
}

export const MappingModule = () => {
  const navigate = useNavigate();
  const { isAuthorized } = useAuth();
  const canManage = isAuthorized(PRIVILEGES.Settings.MANAGE_DISCRIMINANT);
  const [searchText, setSearchText] = useState('');
  const [counterpartyFilter, setCounterpartyFilter] =
    useState(ALL_COUNTERPARTIES);

  const {
    data: groupedData,
    count,
    loading,
    getExternalServiceCodesGrouped,
  } = useGetExternalServiceCodesGrouped();

  const [params, setParams] = useState<MappingParams>({
    pageNumber: 1,
    resultSize: 10,
  });

  useEffect(() => {
    getExternalServiceCodesGrouped(params);
  }, [getExternalServiceCodesGrouped, params]);

  const data = useMemo(() => mapToShippingServices(groupedData), [groupedData]);

  const goToForm = () => navigate(reactPaths.mappingForm);
  const openCreateActivity = () =>
    window.open('/tables/activities/new', '_blank', 'noopener,noreferrer');

  if (!groupedData && loading) {
    return (
      <Box>
        <LoadingScreen />
      </Box>
    );
  }

  return (
    <Box
      alignItems='center'
      display='flex'
      flexDirection='column'
      paddingTop='20px'
      sx={{ mx: 4 }}
    >
      <Box sx={{ width: '100%', maxWidth: '1524px' }}>
        {canManage && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <StyledButton $primary onClick={goToForm}>
              {buttons.addExternalActivity}
            </StyledButton>
            <StyledButton $primary onClick={openCreateActivity}>
              {buttons.addInternalActivity}
            </StyledButton>
          </Box>
        )}

        <Paper
          sx={{
            width: '100%',
            margin: '1rem 0',
            borderTop: '1px solid #e4e4e4',
          }}
        >
          {/* <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1.5,
            }}
          >
            <TextField
              select
              label={mainColumnHeaders.counterparty}
              value={counterpartyFilter}
              onChange={(event) => setCounterpartyFilter(event.target.value)}
              size='small'
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={ALL_COUNTERPARTIES}>
                {allCounterparties}
              </MenuItem>
            </TextField>
            <TextField
              placeholder={searchPlaceholder}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              size='small'
              sx={{ minWidth: 200 }}
            />
          </Box>
          <Divider /> */}
          <MappingTable
            data={data}
            count={count}
            params={params}
            setParams={setParams}
          />
        </Paper>

        {canManage && (
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <StyledButton $primary variant='contained' onClick={goToForm}>
              {buttons.addExternalActivity}
            </StyledButton>
            <StyledButton
              $primary
              variant='contained'
              onClick={openCreateActivity}
            >
              {buttons.addInternalActivity}
            </StyledButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};
